import { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Select, MenuItem, FormControl, InputLabel, Stack, Chip, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { PageHeader } from '@/components/navigation/PageHeader';
import { useNavigate } from 'react-router-dom';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { usePageMeta } from '@/hooks/usePageMeta';
import { attendanceApi } from '@/api/attendance.api';
import { tokens } from '@/theme/tokens';
import { CheckCircle, XCircle, Clock, Timer, Calendar, CalendarDays, FileText, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

export function MyAttendanceHistoryPage() {
  usePageMeta('My Attendance History | Attendance', [
    { label: 'Attendance', path: '/attendance' },
    { label: 'My Attendance History' },
  ]);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [summary, setSummary] = useState(null);

  const currentDate = new Date();
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());

  useEffect(() => {
    fetchHistory();
  }, [month, year]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await attendanceApi.getMyAttendanceHistory(month, year);
      if (res?.success) {
        setHistoryData(res.attendance || []);
        setSummary(res.summary || null);
      } else {
        setHistoryData([]);
        setSummary(null);
      }
    } catch (error) {
      toast.error('Failed to load attendance history');
      setHistoryData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeStatus = (val) => {
    switch (val) {
      case 'PRESENT': return 'success';
      case 'ABSENT': return 'error';
      case 'LATE': return 'warning';
      case 'HALF_DAY': return 'info';
      default: return 'default';
    }
  };

  const formatTime = (value) =>
    value
      ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : '--:--';

  const columns = [
    {
      field: 'attendance_date',
      headerName: 'Date',
      flex: 1.2,
      minWidth: 130,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', height: '100%' }}>
          <CalendarDays size={16} color={tokens.colors.text.secondary} />
          <Typography variant="body2" fontWeight={600}>
            {params.value || '--'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'attendance_status',
      headerName: 'Status',
      flex: 1,
      minWidth: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <StatusBadge status={getBadgeStatus(params.value)} label={params.value || 'N/A'} />
        </Box>
      )
    },
    {
      field: 'check_in_time',
      headerName: 'Check In',
      flex: 1,
      minWidth: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Typography variant="body2" fontWeight={500}>
            {formatTime(params.value)}
          </Typography>
        </Box>
      )
    },
    {
      field: 'check_out_time',
      headerName: 'Check Out',
      flex: 1,
      minWidth: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Typography variant="body2" fontWeight={500}>
            {formatTime(params.value)}
          </Typography>
        </Box>
      )
    },
    {
      field: 'worked_hours',
      headerName: 'Hours',
      flex: 0.8,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Typography variant="body2" fontWeight={500}>
            {`${params.value ?? 0} hrs`}
          </Typography>
        </Box>
      )
    },
    {
      field: 'attendance_method',
      headerName: 'Method',
      flex: 1,
      minWidth: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Chip
            label={params.value || 'QR_STATIC'}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        </Box>
      )
    },
    {
      field: 'is_late',
      headerName: 'Late?',
      flex: 0.8,
      minWidth: 90,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          {params.value ? (
            <StatusBadge status="warning" label="Yes" />
          ) : (
            <Typography variant="body2" color="text.secondary">No</Typography>
          )}
        </Box>
      )
    },
  ];

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
      <PageHeader
        title="My Attendance History"
        subtitle="View your monthly personal attendance records"
        actions={
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/attendance/my-attendance')}
            sx={{ bgcolor: 'background.paper' }}
          >
            Back to My Attendance
          </Button>
        }
      />

      {/* FULL WIDTH STAT CARDS */}
      <Grid container spacing={2.5} sx={{ width: '100%', m: 0 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }} sx={{ pl: '0 !important' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Present
                </Typography>
                <Typography variant="h4" fontWeight={700} color="success.main" sx={{ mt: 0.5 }}>
                  {summary?.present ?? 0}
                </Typography>
              </Box>
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'success.lighter', color: 'success.main', display: 'flex' }}>
                <CheckCircle size={22} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Absent
                </Typography>
                <Typography variant="h4" fontWeight={700} color="error.main" sx={{ mt: 0.5 }}>
                  {summary?.absent ?? 0}
                </Typography>
              </Box>
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'error.lighter', color: 'error.main', display: 'flex' }}>
                <XCircle size={22} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Late
                </Typography>
                <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ mt: 0.5 }}>
                  {summary?.late ?? 0}
                </Typography>
              </Box>
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'warning.lighter', color: 'warning.main', display: 'flex' }}>
                <Clock size={22} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Half Day
                </Typography>
                <Typography variant="h4" fontWeight={700} color="info.main" sx={{ mt: 0.5 }}>
                  {summary?.half_day ?? 0}
                </Typography>
              </Box>
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'info.lighter', color: 'info.main', display: 'flex' }}>
                <Timer size={22} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Leave
                </Typography>
                <Typography variant="h4" fontWeight={700} color="text.secondary" sx={{ mt: 0.5 }}>
                  {summary?.on_leave ?? 0}
                </Typography>
              </Box>
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'grey.100', color: 'text.secondary', display: 'flex' }}>
                <Calendar size={22} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <WidgetContainer
        title="Monthly Attendance Log"
        subtitle="Detailed breakdown of your attendance entries for the selected period"
        noPadding
        action={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={(e) => setMonth(Number(e.target.value))}>
                {months.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(Number(e.target.value))}>
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        }
      >
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={historyData}
            columns={columns}
            getRowId={(row) => row.id || row.attendance_date || Math.random()}
            loading={loading}
            autoHeight
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 6, color: 'text.secondary', gap: 1 }}>
                  <FileText size={36} style={{ opacity: 0.4 }} />
                  <Typography variant="body2" fontWeight={500}>
                    No attendance records found for this month
                  </Typography>
                </Box>
              )
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-row:hover': { backgroundColor: `${tokens.colors.primary.main}08` },
            }}
          />
        </Box>
      </WidgetContainer>
    </Box>
  );
}
